import { useBackdropDismiss } from '@/lib/useBackdropDismiss'
import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell, ArrowDownLeft, RotateCcw, X } from 'lucide-react'
import { billingApi } from '@/api/billing'
import { useAuthStore } from '@/store/auth'
import { errorMessage } from '@/api/client'
import { closeDialog } from '@/lib/dialogMotion'

const titles: Record<string, string> = {
  paid: '充值成功',
  requested: '收到退款申请',
  processing: '退款处理中',
  completed: '退款成功',
  refunded: '退款成功',
  rejected: '退款已拒绝',
  failed: '退款失败',
  open: '团队风险待处理',
  pending_review: '团队风险待复核',
}
export default function BillingNotifications() {
  const backdrop = useBackdropDismiss()
  const admin = useAuthStore((s) => s.admin)
  const key = `billing-notifications-read:${admin?.id}`
  const [seen, setSeen] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(key) || '[]')
      return Array.isArray(v) ? v : []
    } catch {
      return []
    }
  })
  const [tab, setTab] = useState('all')
  const [open, setOpen] = useState(false)
  const dialog = useRef<HTMLDialogElement>(null)
  const navigate = useNavigate()
  const feed = useQuery({
    queryKey: ['billing-notifications', admin?.id],
    queryFn: billingApi.notifications,
    refetchInterval: 15000,
    retry: 1,
  })
  const items = feed.data?.items || []
  const unread = items.filter((n) => !seen.includes(n.id)).length
  function mark(ids: string[]) {
    const next = [...new Set([...seen, ...ids])].slice(-1000)
    setSeen(next)
    try {
      localStorage.setItem(key, JSON.stringify(next))
    } catch {
      /* Read state still works for this session. */
    }
  }
  return (
    <div className="billing-notifications lg:mt-auto lg:pt-6">
      <button
        type="button"
        aria-label={`后台通知，${unread}条未读`}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="notification-trigger"
        onClick={() => {
          setOpen(true)
          dialog.current?.showModal()
          void feed.refetch()
        }}
      >
        <Bell size={18} />
        <span>通知中心</span>
        {unread > 0 && (
          <span className="notification-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <dialog
        {...backdrop}
        ref={dialog}
        className="billing-notification-dialog"
        aria-label="后台通知"
        onClose={() => setOpen(false)}
        onCancel={(e) => {
          e.preventDefault()
          void closeDialog(dialog.current)
        }}
      >
        <header className="flex items-center justify-between border-b p-5">
          <div>
            <h2 className="font-semibold">通知中心</h2>
            <p className="mt-1 text-xs text-slate-500">
              充值、退款与团队风险 · {unread} 条未读
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭通知"
            className="rounded-lg p-2 hover:bg-slate-100"
            onClick={() => void closeDialog(dialog.current)}
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex items-center gap-1 border-b px-4 py-3">
          {(
            [
              ['all', '全部'],
              ['recharge', '充值'],
              ['refund', '退款'],
              ['risk', '团队风险'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={tab === id}
              className={`rounded-lg px-3 py-1.5 text-sm ${tab === id ? 'bg-brand-50 text-brand-700' : 'text-slate-500'}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            disabled={!unread}
            className="ml-auto text-xs text-brand-600 disabled:opacity-40"
            onClick={() => mark(items.map((n) => n.id))}
          >
            全部已读
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {feed.isError && (
            <p role="alert" className="p-4 text-sm text-red-700">
              {errorMessage(feed.error)}{' '}
              <button onClick={() => void feed.refetch()}>重试</button>
            </p>
          )}
          {feed.isPending ? (
            <p className="p-8 text-center text-sm text-slate-500">加载中…</p>
          ) : (
            !items.filter((n) => tab === 'all' || n.kind === tab).length && (
              <p className="p-8 text-center text-sm text-slate-500">暂无通知</p>
            )
          )}
          {items
            .filter((n) => tab === 'all' || n.kind === tab)
            .map((n) => (
              <button
                key={n.id}
                type="button"
                className="notification-item"
                onClick={() => {
                  mark([n.id])
                  dialog.current?.close()
                  navigate(
                    n.kind === 'risk'
                      ? `/teams?team=${encodeURIComponent(n.orderId)}`
                      : n.kind === 'refund'
                      ? `/orders?tab=refunds&refundOrder=${encodeURIComponent(n.orderNo)}`
                      : `/orders?detail=${encodeURIComponent(n.orderId)}`,
                  )
                }}
              >
                <span
                  className={`notification-icon ${n.kind === 'refund' ? 'text-amber-700 bg-amber-50' : 'text-brand-600 bg-brand-50'}`}
                >
                  {n.kind === 'refund' ? (
                    <RotateCcw size={17} />
                  ) : (
                    <ArrowDownLeft size={18} />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2 text-sm font-medium">
                    <span>{titles[n.status] || n.status}</span>
                    <span>{n.kind === 'risk' ? '需核验' : `¥ ${(n.amountFen / 100).toFixed(2)}`}</span>
                  </span>
                  <span className="mt-1 block text-sm text-slate-500">
                    {n.productTitle}
                  </span>
                  <span className="mt-1 block break-all font-mono text-[11px] text-slate-400">
                    {n.orderNo}
                  </span>
                  <span className="mt-2 block text-xs text-slate-400">
                    {new Date(n.occurredAt).toLocaleString('zh-CN', {
                      hour12: false,
                    })}
                  </span>
                </span>
                {!seen.includes(n.id) && (
                  <span
                    className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600"
                    aria-label="未读"
                  />
                )}
              </button>
            ))}
        </div>
        <footer className="border-t px-5 py-3 text-xs text-slate-400">
          最近 100 条动态，每 15 秒更新 · 已读状态保存在本机
        </footer>
      </dialog>
    </div>
  )
}
